import React, { Component, ReactNode } from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { logger } from '@/lib/logger';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
  errorInfo?: React.ErrorInfo;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    // Ignore DOM manipulation errors that occur during error recovery
    if (error.message?.includes('insertBefore') || error.message?.includes('Node')) {
      console.warn('Ignoring DOM manipulation error during React reconciliation:', error.message);
      return { hasError: false };
    }
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    logger.error('ErrorBoundary caught an error', { 
      error: error.message, 
      stack: error.stack,
      componentStack: errorInfo.componentStack,
      timestamp: new Date().toISOString()
    });
    
    // Log to analytics if available
    if (typeof window !== 'undefined' && (window as any).gtag) {
      (window as any).gtag('event', 'exception', {
        description: error.toString(),
        fatal: false
      });
    }

    // Detect chunk/module loading errors (includes Vite dynamic imports)
    const isChunkError = error.message?.includes('Loading chunk') || 
                        error.message?.includes('ChunkLoadError') ||
                        error.message?.includes('Loading CSS chunk') ||
                        error.message?.includes('Failed to fetch dynamically imported module') ||
                        error.message?.includes('Importing a module script failed') ||
                        error.message?.includes('Failed to fetch');
    
    // Add reload protection - don't reload too frequently
    const lastReload = sessionStorage.getItem('lastErrorReload');
    const now = Date.now();
    const timeSinceLastReload = lastReload ? now - parseInt(lastReload) : Infinity;
    
    if (isChunkError && timeSinceLastReload > 10000) { // 10 seconds minimum between reloads
      logger.info('Chunk/module load error detected, clearing cache and reloading', { 
        errorMessage: error.message,
        timeSinceLastReload 
      });
      sessionStorage.setItem('lastErrorReload', now.toString());
      
      // Clear service worker caches if available
      if ('caches' in window) {
        caches.keys().then(names => {
          names.forEach(name => caches.delete(name));
        });
      }
      
      // Force reload from server
      window.location.reload();
      return; // Don't update state, we're reloading
    } else if (isChunkError) {
      logger.warn('Chunk error detected but reload blocked due to frequency protection', {
        timeSinceLastReload,
        errorMessage: error.message
      });
    }

    this.setState({
      error,
      errorInfo
    });
  }

  handleReload = () => {
    window.location.reload();
  };

  handleRetry = () => {
    this.setState({ hasError: false, error: undefined, errorInfo: undefined });
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      const isChunkError = this.state.error?.message?.includes('Loading chunk') || 
                          this.state.error?.message?.includes('ChunkLoadError');

      return (
        <div className="min-h-screen flex items-center justify-center p-4 bg-background">
          <Card className="max-w-md w-full p-6 text-center space-y-4">
            <div className="flex justify-center">
              <AlertTriangle className="h-12 w-12 text-destructive" />
            </div>
            
            <div className="space-y-2">
              <h2 className="text-xl font-semibold text-foreground">
                {isChunkError ? 'Mise à jour en cours...' : 'Une erreur est survenue'}
              </h2>
              <p className="text-muted-foreground text-sm">
                {isChunkError 
                  ? 'L\'application se recharge automatiquement avec la dernière version.'
                  : 'Nous nous excusons pour ce désagrément. Veuillez réessayer.'
                }
              </p>
            </div>

            {isChunkError ? (
              <div className="flex items-center justify-center space-x-2">
                <RefreshCw className="h-4 w-4 animate-spin" />
                <span className="text-sm text-muted-foreground">Rechargement dans 3 secondes...</span>
              </div>
            ) : (
              <div className="space-y-2">
                <Button onClick={this.handleReload} className="w-full">
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Recharger la page
                </Button>
                <Button variant="outline" onClick={this.handleRetry} className="w-full">
                  Réessayer
                </Button>
              </div>
            )}

            {process.env.NODE_ENV === 'development' && this.state.error && (
              <details className="text-left text-xs text-muted-foreground mt-4">
                <summary className="cursor-pointer">Détails de l'erreur (dev)</summary>
                <pre className="mt-2 whitespace-pre-wrap overflow-auto">
                  {this.state.error.toString()}
                  {this.state.errorInfo?.componentStack}
                </pre>
              </details>
            )}
          </Card>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;